import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from '../../../../PrevNextButtons';

import { FeeStatus, SchoolType } from 'types';
import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';
import { useTranslation } from "react-i18next";

export default function Diversity() {
    const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { application } = useSelector((state: ApplicationState) => state.apply);
  const { nextRoute, previousRoute } = useTaskNavigation();

  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/diversity-inclusion');

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  const { initial, current } = getApplicationData(application);

  const {
    ethnicity,
    hasDisability,
    isDisabilityStudentAllowance,
    hasBeenInCare,
    hasParentsInHE,
    lastSchoolDescription,
    occupation,
    disabilityTypeOther,
    disabilityTypes,
    careDetails,
    careStartDate,
    careEndDate,
    feeStatus,
  } = current;

  const {
    ethnicity: initialEthnicity,
    hasDisability: initialHasDisability,
    isDisabilityStudentAllowance: initialIsDisabilityStudentAllowance,
    hasBeenInCare: initialHasBeenInCare,
    hasParentsInHE: initialHasParentsInHE,
    lastSchoolDescription: initialLastSchoolDescription,
    occupation: initialOccupation,
    disabilityTypeOther: initialDisabilityTypeOther,
    disabilityTypes: initialDisabilityTypes,
    careDetails: initialCareDetails,
    careStartDate: initialCareStartDate,
    careEndDate: initialCareEndDate,
    feeStatus: initialFeeStatus,
  } = initial;

  const showFeeStatusFields = feeStatus === FeeStatus.Home || initialFeeStatus === FeeStatus.Home;
  const showCareFields = hasBeenInCare || initialHasBeenInCare;
  const showDisabilityFields = hasDisability || initialHasDisability;
  // show other disability field if "Other" is selected or was previously selected, and the user also has a disability
  const showOtherDisibilityFields =
    (hasDisability && disabilityTypeOther) || (initialHasDisability && initialDisabilityTypeOther);
  // show list of selected disabilities if user has a disability alongside selected disability types
  const disabilities: string = hasDisability ? disabilityTypes?.join(', ') : undefined;
  const initialDisabilities: string = initialHasDisability ? initialDisabilityTypes?.join(', ') : undefined;

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.DIVERSITY_AND_INCLUSION} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          {t('common:data')}
                          </CustomHeading>

        <Box>
          <Field leftLabel="Ethnicity" value={ethnicity} originalValue={initialEthnicity} />
          {showDisabilityFields && (
            <Field leftLabel="Disabilities" value={disabilities} originalValue={initialDisabilities} />
          )}
          {showOtherDisibilityFields && (
            <Field
              leftLabel="Other disability"
              value={disabilityTypeOther}
              originalValue={initialDisabilityTypeOther}
            />
          )}
          {showFeeStatusFields && (
            <>
              <Field
                leftLabel="Disabled Students Allowance"
                value={isDisabilityStudentAllowance}
                originalValue={initialIsDisabilityStudentAllowance}
              />
              <Field
                leftLabel="Been in care"
                value={hasBeenInCare ?? false}
                originalValue={initialHasBeenInCare ?? false}
              />
              {showCareFields && (
                <>
                  <Field
                    leftLabel="Start"
                    value={formatYearMonthDate(careStartDate)}
                    originalValue={formatYearMonthDate(initialCareStartDate)}
                  />
                  <Field
                    leftLabel="End"
                    value={formatYearMonthDate(careEndDate)}
                    originalValue={formatYearMonthDate(initialCareEndDate)}
                  />
                  <Field leftLabel="Reason" value={careDetails} originalValue={initialCareDetails} />
                </>
              )}
              <Field
                leftLabel="Last education type"
                value={SchoolType[lastSchoolDescription]}
                originalValue={SchoolType[initialLastSchoolDescription]}
              />
              <Field
                leftLabel="Parents in higher education"
                value={hasParentsInHE}
                originalValue={initialHasParentsInHE}
              />
              <Field leftLabel="Occupational background?" value={occupation} originalValue={initialOccupation} />
            </>
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
